import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { CLINIC } from "../lib/api";

const NAV = [
  { to: "/", label: "Inicio" },
  {
    label: "Servicios",
    children: [
      { to: "/implantes-dentales-calafell", label: "Implantes dentales" },
      { to: "/ortodoncia-invisible-calafell", label: "Ortodoncia invisible" },
      { to: "/estetica-dental", label: "Estética dental" },
      { to: "/odontopediatria", label: "Odontopediatría" },
      { to: "/urgencias-dentales-calafell", label: "Urgencias dentales" },
    ],
  },
  { to: "/equipo", label: "Equipo" },
  { to: "/opiniones", label: "Opiniones" },
  { to: "/blog", label: "Blog" },
  { to: "/contacto", label: "Contacto" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all ${
        scrolled ? "glass-header shadow-sm" : "bg-white"
      } border-b border-slate-100`}
    >
      <div className="container-max flex items-center justify-between py-4">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2.5 group"
          data-testid="header-logo"
        >
          <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center text-white font-bold text-lg shadow-sm group-hover:bg-orange-700 transition-colors">
            P
          </div>
          <div className="leading-tight">
            <div className="font-bold text-slate-900 text-lg tracking-tight">
              Clínica Dental Pina
            </div>
            <div className="text-xs text-slate-500 font-medium">
              Calafell · desde 1984
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  data-testid="nav-services-dropdown"
                  className="flex items-center gap-1 px-4 py-2 rounded-full text-slate-700 hover:text-teal-700 hover:bg-teal-50 text-sm font-medium transition-colors"
                >
                  {item.label}
                  <ChevronDown className="w-4 h-4" />
                </button>
                {servicesOpen && (
                  <div className="absolute top-full left-0 pt-2 w-64">
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-2">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-service-${c.to.replace(/\//g, "-")}`}
                          className="block px-4 py-2.5 rounded-xl text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.to.replace(/\//g, "-") || "home"}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? "text-teal-700 bg-teal-50"
                      : "text-slate-700 hover:text-teal-700 hover:bg-teal-50"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${CLINIC.phone}`}
            data-testid="header-phone"
            className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-teal-700 transition-colors"
          >
            <Phone className="w-4 h-4" />
            {CLINIC.phoneDisplay}
          </a>
          <button
            onClick={() => navigate("/contacto")}
            data-testid="header-cta-appointment"
            className="btn-primary"
          >
            Pide tu cita gratis
          </button>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100"
          aria-label="Menú"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
          <div className="container-max py-4 flex flex-col gap-1">
            <Link to="/" onClick={() => setOpen(false)} className="px-4 py-3 rounded-xl text-slate-700 hover:bg-teal-50 font-medium" data-testid="mobile-nav-home">
              Inicio
            </Link>
            <div className="px-4 py-2 text-xs uppercase tracking-wider text-slate-400 font-semibold">
              Servicios
            </div>
            {NAV[1].children.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav${c.to.replace(/\//g, "-")}`}
                className="px-6 py-2.5 rounded-xl text-slate-700 hover:bg-teal-50 text-sm"
              >
                {c.label}
              </Link>
            ))}
            <div className="h-px bg-slate-100 my-2" />
            {NAV.slice(2).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav${item.to.replace(/\//g, "-")}`}
                className="px-4 py-3 rounded-xl text-slate-700 hover:bg-teal-50 font-medium"
              >
                {item.label}
              </Link>
            ))}
            <div className="flex gap-2 mt-4 px-2">
              <a href={`tel:${CLINIC.phone}`} className="btn-secondary flex-1 justify-center">
                <Phone className="w-4 h-4" /> Llamar
              </a>
              <Link
                to="/contacto"
                onClick={() => setOpen(false)}
                className="btn-primary flex-1 justify-center"
                data-testid="mobile-cta-appointment"
              >
                Pide cita
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
